// PUBLIC end-user report page (route: /r/:token). Opened from an SMS link on a
// phone — no login, no Cognito. Renders a pre-aggregated snapshot (lifetime
// summary tiles + a few pie/donut breakdowns) fetched by token. Mobile-first and
// dependency-light (inline-SVG MiniDonut, no Plotly) so it loads instantly.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MiniDonut } from "../../lib/charts/MiniCharts";
import { applyBrandToDocument, SIDENERD_BRAND } from "../../lib/branding/appBrands";
import { brandVars } from "../../lib/branding/brandVars";
import { fetchSnapshot, type UserReportSnapshot } from "../../lib/reports/snapshot";

function fmtStat(value: number | string, unit?: string): string {
  const v = typeof value === "number" ? value.toLocaleString() : value;
  if (!unit) return v;
  return unit === "$" ? `$${v}` : `${v} ${unit}`;
}

/**
 * The report itself (tiles + donut breakdowns), independent of how the snapshot
 * was obtained. The public /r/:token route fetches by token; the admin panel's
 * "User view" tab builds one client-side to preview what a member sees.
 */
export function UserReportCard({ snap }: { snap: UserReportSnapshot }) {
  const generated = new Date(snap.generatedAt);

  return (
    <div className="sn-ureport">
      <header className="sn-ureport-head">
        {snap.brand?.logoUrl && <img className="sn-ureport-logo" src={snap.brand.logoUrl} alt="" />}
        <div>
          <h1 className="sn-ureport-title">{snap.title}</h1>
          <p className="sn-ureport-sub">
            {snap.person?.firstName ? `${snap.person.firstName} · ` : ""}
            {snap.appName}
          </p>
        </div>
      </header>

      <section className="sn-ureport-section">
        <h2 className="sn-ureport-band">{snap.periodLabel ?? "Summary"}</h2>
        <div className="sn-ureport-tiles">
          {snap.summary.map((s, i) => (
            <div key={i} className="sn-ureport-tile">
              <div className="sn-ureport-tile-value">{fmtStat(s.value, s.unit)}</div>
              <div className="sn-ureport-tile-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {snap.breakdowns.map((b, i) => {
        const total = b.slices.reduce((a, s) => a + s.value, 0);
        const isCurrency = b.unit === "$";
        const centerTop = total
          ? isCurrency
            ? `$${Math.round(total).toLocaleString()}`
            : Math.round(total).toLocaleString()
          : undefined;
        return (
          <section key={i} className="sn-ureport-section">
            <h2 className="sn-ureport-band">{b.title}</h2>
            <div className="sn-ureport-card">
              <MiniDonut
                slices={b.slices}
                unit={b.unit}
                centerTop={centerTop}
                centerSub={total ? (isCurrency ? "total" : b.unit) : undefined}
                emptyText="No activity in this category yet"
              />
            </div>
          </section>
        );
      })}

      <footer className="sn-ureport-foot">
        {snap.footerNote && <p>{snap.footerNote}</p>}
        <p className="sn-ureport-hint">
          As of {generated.toLocaleDateString()} · Powered by Side Nerd Apps
        </p>
      </footer>
    </div>
  );
}

export function UserReport() {
  const { token = "demo" } = useParams();
  const [snap, setSnap] = useState<UserReportSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setSnap(null);
    setError(null);
    fetchSnapshot(token)
      .then((s) => live && setSnap(s))
      .catch((e) => live && setError((e as Error).message));
    return () => {
      live = false;
    };
  }, [token]);

  useEffect(() => {
    if (snap) {
      applyBrandToDocument({ appName: `${snap.appName} — ${snap.title}`, ...snap.brand });
    }
  }, [snap]);

  if (error) {
    return (
      <div className="sn-ureport-wrap">
        <div className="sn-ureport sn-ureport-msg">
          <img className="sn-ureport-sn" src={SIDENERD_BRAND.logoUrl} alt="Side Nerd Apps" />
          <h1>Report unavailable</h1>
          <p>{error}</p>
          <p className="sn-ureport-hint">Reply to your text thread to request a fresh link.</p>
        </div>
      </div>
    );
  }

  if (!snap) {
    return (
      <div className="sn-ureport-wrap">
        <div className="sn-ureport sn-ureport-msg">
          <p>Loading your report…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sn-ureport-wrap" style={brandVars(snap.brand ?? {})}>
      <UserReportCard snap={snap} />
    </div>
  );
}
